import axios from "axios";

const api = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_URL,
	withCredentials: true,
});

export const openApi = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_URL,
	withCredentials: false,
	headers: {
		'Content-Type': 'application/json',
	}
});

// ==========================================
// Controle de sessão e refresh token
// ==========================================
let isSessionExpired = false;
let isRefreshing = false;
let isBlocked = false; // Bloqueia TODAS as requests enquanto refresh está em andamento
let failedQueue = [];

const processQueue = (error, token = null) => {
	failedQueue.forEach(({ resolve, reject }) => {
		if (error) {
			reject(error);
		} else {
			resolve(token);
		}
	});
	failedQueue = [];
};

export const setSessionExpired = (value) => {
	isSessionExpired = value;
};

export const getSessionExpired = () => isSessionExpired;

const isPublicPage = () => {
	const path = window.location.pathname;
	return path.includes("/login") || path.includes("/signup") || path.includes("/forgot");
};

const forceLogout = () => {
	isSessionExpired = true;
	isRefreshing = false;
	isBlocked = false;
	processQueue(new Error("Session expired"));
	localStorage.removeItem("token");
	api.defaults.headers.Authorization = undefined;

	// Desconectar socket para parar reconexões com token expirado
	try {
		const SocketWorker = require("./SocketWorker").default;
		if (SocketWorker && SocketWorker.instance) {
			SocketWorker.instance.disconnect();
		}
	} catch (e) {
		// Silenciar erro se SocketWorker não estiver disponível
	}

	if (!isPublicPage()) {
		window.location.href = "/login";
	}
};

// Request interceptor
api.interceptors.request.use(
	(config) => {
		// Bloquear requests se sessão expirou OU se está aguardando refresh
		if (isSessionExpired || isBlocked) {
			const isAuthUrl = config.url?.includes("/auth/login") || config.url?.includes("/auth/logout") || config.url?.includes("/auth/refresh_token");
			if (!isAuthUrl) {
				return Promise.reject(new Error("Session expired or blocked"));
			}
		}

		const token = localStorage.getItem("token");
		if (token && !isPublicPage()) {
			try {
				config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
			} catch (e) {
				localStorage.removeItem("token");
			}
		}
		return config;
	},
	(error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		const status = error?.response?.status;

		// 429 — rate limit, forçar logout imediato
		if (status === 429) {
			forceLogout();
			return Promise.reject(error);
		}

		// Em páginas públicas, não tentar refresh
		if (isPublicPage()) {
			if (status === 401 || status === 403) {
				localStorage.removeItem("token");
				api.defaults.headers.Authorization = undefined;
			}
			return Promise.reject(error);
		}

		// Sessão já marcada como expirada — rejeitar tudo
		if (isSessionExpired) {
			return Promise.reject(error);
		}

		// Se o próprio refresh falhou — sessão morta
		if (originalRequest?.url?.includes("/auth/refresh_token")) {
			forceLogout();
			return Promise.reject(error);
		}

		// 401/403 — tentar refresh UMA vez
		if ((status === 401 || status === 403) && !originalRequest._retry) {
			originalRequest._retry = true;

			// Bloquear TODAS as novas requests imediatamente
			isBlocked = true;

			// Se já está fazendo refresh, enfileirar e esperar
			if (isRefreshing) {
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				}).then((token) => {
					originalRequest.headers["Authorization"] = `Bearer ${token}`;
					return api(originalRequest);
				});
			}

			isRefreshing = true;

			try {
				const { data } = await api.post("/auth/refresh_token");
				const newToken = data.token;

				localStorage.setItem("token", JSON.stringify(newToken));
				api.defaults.headers.Authorization = `Bearer ${newToken}`;
				isSessionExpired = false;
				isRefreshing = false;
				isBlocked = false; // Desbloquear requests após refresh bem-sucedido

				// Resolver todas as requests que estavam esperando
				processQueue(null, newToken);

				originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
				return api(originalRequest);
			} catch (refreshError) {
				forceLogout();
				return Promise.reject(refreshError);
			}
		}

		return Promise.reject(error);
	}
);

export default api;
